import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Clock, AlertTriangle, ArrowRight, X } from "lucide-react";
import { UpgradeDialog } from "@/components/UpgradeDialog";

export const TrialBanner = () => {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const load = () => api.get("/me/subscription").then((r) => {
    setSub(r.data);
    if (r.data?.is_trial) {
      const key = `trial_dismissed_${r.data.tenant_id}_${r.data.trial_ends_at}`;
      if (localStorage.getItem(key) === "1") setDismissed(true);
    }
  }).catch(() => {});

  useEffect(() => {
    load();
    window.addEventListener("recoveros:usage-changed", load);
    return () => window.removeEventListener("recoveros:usage-changed", load);
  }, []);

  if (!sub || !sub.is_trial || dismissed) return null;

  const expired = sub.expired;
  const days = sub.days_left ?? 0;
  const accUsed = sub.accounts_used ?? 0;
  const accLimit = sub.account_limit || 0;
  const usrUsed = sub.users_used ?? 0;
  const usrLimit = sub.user_limit || 0;

  const dismiss = () => {
    localStorage.setItem(`trial_dismissed_${sub.tenant_id}_${sub.trial_ends_at}`, "1");
    setDismissed(true);
  };

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 px-4 md:px-6 py-2.5 text-sm border-b sticky top-16 z-10 ${
        expired ? "bg-red-50 border-red-200 text-red-800" : "bg-[#0B3D91]/5 border-[#0B3D91]/15 text-[#0B3D91]"
      }`}
      data-testid="trial-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        {expired ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <Clock className="h-4 w-4 shrink-0" />}
        <span className="font-medium truncate" data-testid="trial-banner-text">
          {expired ? (
            <>Your free trial has ended. Upgrade to keep adding accounts and team members.</>
          ) : (
            <>
              <strong>7-Day Trial Active</strong> · <strong data-testid="trial-days-remaining">{days} day{days === 1 ? "" : "s"} remaining</strong>
              {accLimit ? <span className="hidden sm:inline"> · {accUsed}/{accLimit} accounts</span> : null}
              {usrLimit ? <span className="hidden md:inline"> · {usrUsed}/{usrLimit} users</span> : null}
            </>
          )}
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={() => setUpgradeOpen(true)}
          className={`inline-flex items-center gap-1.5 rounded-none px-3 py-1.5 font-semibold text-white transition-colors ${
            expired ? "bg-red-600 hover:bg-red-700" : "bg-[#16A34A] hover:bg-[#138a3e]"
          }`}
          data-testid="trial-upgrade-btn"
        >
          {expired ? "Upgrade" : "Upgrade Now"} <ArrowRight className="h-3.5 w-3.5" />
        </button>
        {!expired && (
          <button onClick={dismiss} className="opacity-60 hover:opacity-100" data-testid="trial-banner-dismiss" aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} currentPlan={sub.is_trial ? null : sub.plan} onUpgraded={load} intendedPlan={sub.intended_plan} intendedBillingPeriod={sub.intended_billing_period} />
    </div>
  );
};
